/**
 * The activated ability menu.
 *
 * A permanent with more than one way to be used cannot be a single click, so
 * clicking one opens this menu. Entries come from `legalActions`, which means
 * an ability the engine will not accept right now is shown greyed with the
 * reason rather than hidden: a player learns the rule instead of wondering
 * where the button went.
 */

import { useEffect, useRef } from 'react';
import type { Action, GameState } from '@/engine';
import { profile } from '@/engine';
import styles from './Battlefield.module.css';

export interface AbilityOption {
  label: string;
  detail?: string;
  action: Action;
  /** Present when the ability exists but cannot be used right now. */
  blockedReason?: string;
}

export interface AbilityMenuProps {
  state: GameState;
  instanceId: string;
  options: AbilityOption[];
  anchor: { x: number; y: number };
  onPick: (option: AbilityOption) => void;
  onClose: () => void;
}

export function AbilityMenu({ state, instanceId, options, anchor, onPick, onClose }: AbilityMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (options.length === 0) return null;

  // Keep the menu inside the viewport without measuring twice.
  const left = Math.min(anchor.x, (typeof window === 'undefined' ? 1200 : window.innerWidth) - 320);
  const top = Math.min(anchor.y, (typeof window === 'undefined' ? 800 : window.innerHeight) - 40 - options.length * 46);

  return (
    <div
      ref={ref}
      className={styles.abilityMenu}
      style={{ left: Math.max(8, left), top: Math.max(8, top) }}
      role="menu"
      aria-label={`${profile(state, instanceId).name} abilities`}
    >
      {options.map((option, i) => (
        <button
          key={i}
          type="button"
          role="menuitem"
          className={styles.abilityItem}
          disabled={Boolean(option.blockedReason)}
          title={option.blockedReason}
          onClick={() => onPick(option)}
        >
          <span className={styles.abilityLabel}>{option.label}</span>
          {option.blockedReason ? (
            <span className={styles.abilityDetail}>{option.blockedReason}</span>
          ) : option.detail ? (
            <span className={styles.abilityDetail}>{option.detail}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}
